import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import QuoteStore from '../../../../stores/QuoteStore';
import UserStore from '../../../../stores/UserStore';
import { message, Modal } from 'antd';

export const useQuoteBootstrap = ({
    id,
    isRapidQuoteFlow,
    goToNewQuote,
}: {
    id?: string;
    isRapidQuoteFlow: boolean;
    goToNewQuote: () => void;
}) => {
    const history = useHistory();

    useEffect(() => {
        if (QuoteStore.rapidQuoteUid && isRapidQuoteFlow) {
            QuoteStore.getQuoteByUid(QuoteStore.rapidQuoteUid)
                .then(async data => {
                    if (UserStore.isAuthenticated) {
                        const userData = UserStore.user;
                        if (data && userData && userData.email !== data.email) {
                            await UserStore.logoutAndClearSession();

                            return true;
                        }

                        QuoteStore.cleanRapidQuoteData();
                        return goToNewQuote();
                    }
                })
                .catch(err => {
                    if (err?.response?.status === 404) {
                        QuoteStore.cleanRapidQuoteData();
                        history.push('/rapid-quote/start/');
                    } else {
                        message.error(
                            `Unable to load your quote. Please contact support for assistance. ${err.message || err}.`
                        );
                    }
                });
            return;
        }

        if (!QuoteStore.rapidQuoteUid && isRapidQuoteFlow && UserStore.isAuthenticated) {
            goToNewQuote();
            return;
        }

        if (id) {
            const cachedQuoteId = QuoteStore.quote?.id ? String(QuoteStore.quote.id) : null;

            if (cachedQuoteId !== id) {
                QuoteStore.cleanQuote();
                QuoteStore.setQuoteLoading(true);
            }

            QuoteStore.getLastDraftQuoteById(id).catch(err => {
                // Sent quotes are no longer drafts. Ask before leaving that link,
                // so a file drop does not fail on an empty page.
                if (err?.response?.data?.code === 'quote_already_submitted') {
                    Modal.confirm({
                        title: 'This quote has already been submitted',
                        content: 'It can no longer be edited. Start a new quote to add files.',
                        okText: 'Start a new quote',
                        cancelText: 'Go to my quotes',
                        onOk: () => goToNewQuote(),
                        onCancel: () => history.push('/my-account/quotes'),
                    });
                    return;
                }
                if (err?.response?.status === 404) {
                    message.error('Unable to open that quote. Starting a new one.');
                    goToNewQuote();
                    return;
                }
                message.error(`Unable to load your last quote. Please contact support for assistance. Error: ${err}.`);
            });
            return;
        }

        const email = UserStore.user?.default_contact?.email || UserStore.user?.email;

        const loadLatestDraftQuote = async () => {
            if (QuoteStore.quote?.type === 'Requote') {
                await QuoteStore.cleanQuote();
            }

            if (!email) return;

            await QuoteStore.getLastQuoteByEmail(email);
        };

        loadLatestDraftQuote().catch(err => {
            if (email) {
                message.error(`Unable to load your last quote. Please contact support for assistance. Error: ${err}.`);
            }
        });
    }, [id, QuoteStore.rapidQuoteUid]);
};
